import { Component, ReactNode } from 'react';
import { clearAuthData } from '@/utils/auth';
import { Button } from '@shelflix/ui-web/components/ui/button';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches React errors and provides recovery options.
 * When an error occurs, it clears potentially corrupted auth data and allows
 * the user to retry or go to login.
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry if available
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  handleClearAndRetry = () => {
    // Clear auth data that might be corrupted
    clearAuthData();

    // Clear any other potentially problematic localStorage items
    try {
      // Keep tenant-related data but clear auth
      const keysToRemove = ['authToken', 'refreshToken'];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // If localStorage is completely broken, try to clear everything
      try {
        localStorage.clear();
      } catch {
        // Nothing more we can do
      }
    }

    // Redirect to login
    window.location.href = '/login';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-background flex min-h-screen flex-col items-center justify-center p-4">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="space-y-2">
              <h1 className="text-foreground text-2xl font-semibold">Erro inesperado</h1>
              <p className="text-muted-foreground">
                Ocorreu um erro ao carregar a aplicação. Isso pode acontecer após uma atualização do sistema.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button onClick={this.handleRefresh} variant="outline" className="w-full">
                Tentar novamente
              </Button>
              <Button onClick={this.handleClearAndRetry} className="w-full">
                Limpar dados e fazer login novamente
              </Button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="bg-destructive/10 mt-4 rounded-md p-3 text-left text-sm">
                <summary className="text-destructive cursor-pointer font-medium">Detalhes do erro (dev)</summary>
                <pre className="text-destructive mt-2 overflow-auto text-xs whitespace-pre-wrap">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
